/**
 * Pure value helpers shared by the form modules (money inputs, month
 * arithmetic). Kept free of DOM and of other UI imports so that both
 * `form-utils.ts` and `lump-list.ts` can use them without a cycle.
 */

/** Thousands grouped with plain spaces; leading zeros are kept: `0001234` → `0 001 234`. */
export function groupThousands(digits: string): string {
  return digits.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
}

export type MoneyParts = { whole: string; fraction: string; point: boolean };

/**
 * Splits a typed amount into ruble and kopeck digits. Both `.` and `,` mark kopecks (the
 * first one; later ones are ignored, at most two kopeck digits are kept). Pasted grouping —
 * `7,000`, `12.345`, `7,000,000`, `7.000.000,50` — is recognised by 1–3 leading digits and
 * three-digit groups (kopecks never have three digits). A long whole part such as
 * `7000000,505` stays kopecks, cut to two digits. Spaces and other characters are dropped.
 */
export function splitMoney(raw: string): MoneyParts {
  const text = raw.replace(/[^\d.,]/g, '');
  const grouping = /^\d{1,3}([.,])\d{3}(?:\1\d{3})*(?:(?!\1)[.,]\d*)?$/.exec(text);
  const body = grouping ? text.split(grouping[1]).join('') : text;
  const index = body.search(/[.,]/);
  if (index < 0) return { whole: body, fraction: '', point: false };
  return { whole: body.slice(0, index), fraction: body.slice(index + 1).replace(/\D/g, '').slice(0, 2), point: true };
}

/** Formats rubles for a text input, kopecks only when there are any: `15500000` → `15 500 000`, `7000000.5` → `7 000 000,50`. */
export function formatMoneyInput(rubles: number): string {
  if (!Number.isFinite(rubles)) return '';
  const cents = Math.round(Math.abs(rubles) * 100);
  const whole = groupThousands(String(Math.floor(cents / 100)));
  const kopecks = cents % 100;
  return `${rubles < 0 && cents > 0 ? '-' : ''}${whole}${kopecks ? `,${String(kopecks).padStart(2, '0')}` : ''}`;
}

export function parseMoney(raw: string): number {
  const { whole, fraction } = splitMoney(raw ?? '');
  if (!whole && !fraction) return 0;
  const value = Number(`${whole || '0'}.${fraction || '0'}`);
  return Number.isFinite(value) ? value : 0;
}

export function rubToCents(value: number): number {
  return Math.round(value * 100);
}

export function monthsBetween(base: string, target: string): number {
  const [baseYear, baseMonth] = base.split('-').map(Number);
  const [targetYear, targetMonth] = target.split('-').map(Number);
  if (!Number.isFinite(baseYear) || !Number.isFinite(targetYear) || !Number.isFinite(baseMonth) || !Number.isFinite(targetMonth)) {
    return 0;
  }
  return (targetYear - baseYear) * 12 + (targetMonth - baseMonth);
}

export function getMonthDate(startDate: string, monthsOffset: number): string {
  const [y, m] = startDate.split('-').map(Number);
  const date = new Date(y, m - 1 + monthsOffset, 1);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  return `${year}-${month}`;
}

export function clamp(value: number, min: number, max: number): number {
  if (!Number.isFinite(value)) return min;
  return Math.min(max, Math.max(min, value));
}
